import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputAdornment, InputLabel,
  MenuItem, Paper, Select, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Search from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1, padding: theme.spacing(2), margin: theme.spacing(1),
  overflowY: "scroll", ...theme.scrollbarStyles,
  borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[1],
}));

const MATCH_LABELS = { contains: "Contém", exact: "Exato", starts_with: "Começa com" };

const TriggerModal = ({ open, onClose, trigger, scripts }) => {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [scriptId, setScriptId] = useState("");
  const [keyword, setKeyword] = useState("");
  const [matchType, setMatchType] = useState("contains");
  const [active, setActive] = useState(true);
  const [maxFires, setMaxFires] = useState("");
  const [cooldownHours, setCooldownHours] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(trigger?.name || "");
    setScriptId(trigger?.scriptId ? String(trigger.scriptId) : "");
    setKeyword(trigger?.keyword || "");
    setMatchType(trigger?.matchType || "contains");
    setActive(trigger?.active !== undefined ? trigger.active : true);
    setMaxFires(trigger?.maxFires !== undefined && trigger?.maxFires !== null ? String(trigger.maxFires) : "");
    setCooldownHours(trigger?.cooldownHours !== undefined && trigger?.cooldownHours !== null ? String(trigger.cooldownHours) : "");
  }, [open, trigger]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!scriptId) { toast.error("Selecione um Speed Mode"); return; }
    if (!keyword.trim()) { toast.error("Palavra-chave é obrigatória"); return; }
    setSaving(true);
    try {
      if (trigger) {
        await api.put(`/scriptTriggers/${trigger.id}`, { name, scriptId: Number(scriptId), keyword, matchType, active, maxFires: maxFires !== "" ? Number(maxFires) : null, cooldownHours: cooldownHours !== "" ? Number(cooldownHours) : null });
        toast.success("Gatilho atualizado!");
      } else {
        await api.post("/scriptTriggers", { name, scriptId: Number(scriptId), keyword, matchType, active, maxFires: maxFires !== "" ? Number(maxFires) : null, cooldownHours: cooldownHours !== "" ? Number(cooldownHours) : null });
        toast.success("Gatilho criado!");
      }
      onClose(true);
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, color: "#fff", py: 1.5 }}>
        <Typography fontWeight={700}>{trigger ? "Editar Gatilho" : "Novo Gatilho"}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            autoFocus fullWidth label="Nome do gatilho *"
            value={name} onChange={e => setName(e.target.value)}
            size="small"
            onKeyDown={e => e.key === "Enter" && handleSave()}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Speed Mode (Script) *</InputLabel>
            <Select value={scriptId} label="Speed Mode (Script) *" onChange={e => setScriptId(e.target.value)}>
              <MenuItem value=""><em>Selecione...</em></MenuItem>
              {scripts.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth label="Palavra-chave *"
            value={keyword} onChange={e => setKeyword(e.target.value)}
            size="small" placeholder="Ex: informações, preço, ajuda"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Tipo de correspondência</InputLabel>
            <Select value={matchType} label="Tipo de correspondência" onChange={e => setMatchType(e.target.value)}>
              <MenuItem value="contains">Contém</MenuItem>
              <MenuItem value="exact">Exato</MenuItem>
              <MenuItem value="starts_with">Começa com</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Limite de disparos por contato</InputLabel>
            <Select value={maxFires} label="Limite de disparos por contato" onChange={e => setMaxFires(e.target.value)}>
              <MenuItem value=""><em>Livre (ilimitado)</em></MenuItem>
              {[1,2,3,5,10].map(n => <MenuItem key={n} value={String(n)}>{n}x por contato</MenuItem>)}
            </Select>
          </FormControl>
          {maxFires !== "" && (
            <FormControl fullWidth size="small">
              <InputLabel>Reenvio após (zera contador)</InputLabel>
              <Select value={cooldownHours} label="Reenvio após (zera contador)" onChange={e => setCooldownHours(e.target.value)}>
                <MenuItem value=""><em>Sem reset automático</em></MenuItem>
                <MenuItem value="1">1 hora</MenuItem>
                <MenuItem value="6">6 horas</MenuItem>
                <MenuItem value="12">12 horas</MenuItem>
                <MenuItem value="24">24 horas</MenuItem>
                <MenuItem value="48">48 horas</MenuItem>
                <MenuItem value="72">72 horas</MenuItem>
                <MenuItem value="168">1 semana</MenuItem>
              </Select>
            </FormControl>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch checked={active} onChange={e => setActive(e.target.checked)} color="primary" />
            <Typography variant="body2">{active ? "Ativo" : "Inativo"}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
        <Button onClick={() => onClose(false)} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} size="small">
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TriggerRow = ({ trigger, onEdit, onDelete, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const script = trigger.script;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BoltIcon sx={{ fontSize: "1rem", color: "#ff6f00" }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>{trigger.name || trigger.keyword}</Typography>
              {trigger.name && trigger.keyword !== trigger.name && (
                <Typography variant="caption" color="textSecondary">{trigger.keyword}</Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip label={script ? script.name : "–"} size="small" color="primary" variant="outlined" />
        </TableCell>
        <TableCell align="center">
          <Chip
            label={MATCH_LABELS[trigger.matchType] || trigger.matchType}
            size="small" variant="outlined"
            sx={{ fontSize: "0.7rem" }}
          />
        </TableCell>
        <TableCell align="center">
          <Switch
            checked={!!trigger.active}
            onChange={() => onToggle(trigger)}
            color="primary" size="small"
          />
        </TableCell>
        <TableCell align="center">
          <Tooltip title="Ver condição">
            <IconButton size="small" onClick={() => setExpanded(v => !v)}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => onEdit(trigger)}>
            <Edit color="info" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(trigger)}>
            <DeleteOutline color="error" />
          </IconButton>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, backgroundColor: "action.hover" }}>
            <Collapse in={expanded}>
              <Box sx={{ py: 1.5, px: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Condição para ativação:
                </Typography>
                <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, backgroundColor: "background.paper", border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.8rem" }}>
                    a mensagem <strong>{(MATCH_LABELS[trigger.matchType] || "").toLowerCase()}</strong>:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic", color: "text.primary" }}>
                    "{trigger.keyword}"
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};


const BulkConfigModal = ({ open, onClose, triggers, onApply }) => {
  const theme = useTheme();
  const [selected, setSelected] = useState([]);
  const [maxFires, setMaxFires] = useState("");
  const [cooldownHours, setCooldownHours] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) { setSelected(triggers.map(t => t.id)); setMaxFires(""); setCooldownHours(""); }
  }, [open, triggers]);

  const toggleAll = () => setSelected(selected.length === triggers.length ? [] : triggers.map(t => t.id));
  const toggle = id => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleApply = async () => {
    if (!selected.length) { toast.error("Selecione ao menos um gatilho"); return; }
    setApplying(true);
    try {
      await Promise.all(selected.map(id =>
        api.put(`/scriptTriggers/${id}`, {
          maxFires: maxFires !== "" ? Number(maxFires) : null,
          cooldownHours: cooldownHours !== "" ? Number(cooldownHours) : null
        })
      ));
      toast.success(`Configuração aplicada em ${selected.length} gatilho(s)`);
      onApply();
    } catch (err) { toastError(err); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, color: "#fff", py: 1.5 }}>
        <Typography fontWeight={700}>Configurar Limites em Lote</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Limite de disparos por contato</InputLabel>
            <Select value={maxFires} label="Limite de disparos por contato" onChange={e => setMaxFires(e.target.value)}>
              <MenuItem value=""><em>Livre (ilimitado)</em></MenuItem>
              {[1,2,3,5,10].map(n => <MenuItem key={n} value={String(n)}>{n}x por contato</MenuItem>)}
            </Select>
          </FormControl>
          {maxFires !== "" && (
            <FormControl fullWidth size="small">
              <InputLabel>Reenvio após (zera contador)</InputLabel>
              <Select value={cooldownHours} label="Reenvio após (zera contador)" onChange={e => setCooldownHours(e.target.value)}>
                <MenuItem value=""><em>Sem reset automático</em></MenuItem>
                <MenuItem value="1">1 hora</MenuItem>
                <MenuItem value="6">6 horas</MenuItem>
                <MenuItem value="12">12 horas</MenuItem>
                <MenuItem value="24">24 horas</MenuItem>
                <MenuItem value="48">48 horas</MenuItem>
                <MenuItem value="72">72 horas</MenuItem>
                <MenuItem value="168">1 semana</MenuItem>
              </Select>
            </FormControl>
          )}
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, maxHeight: 220, overflowY: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.5, borderBottom: "1px solid", borderColor: "divider", backgroundColor: "action.hover" }}>
              <Checkbox size="small" checked={selected.length === triggers.length && triggers.length > 0} indeterminate={selected.length > 0 && selected.length < triggers.length} onChange={toggleAll} />
              <Typography variant="caption" fontWeight={600}>Selecionar todos ({triggers.length})</Typography>
            </Box>
            {triggers.map(t => (
              <Box key={t.id} sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.25 }}>
                <Checkbox size="small" checked={selected.includes(t.id)} onChange={() => toggle(t.id)} />
                <Typography variant="body2">{t.name || t.keyword}</Typography>
                {t.maxFires && <Chip label={t.maxFires + "x"} size="small" sx={{ ml: 1, fontSize: "0.65rem" }} />}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
        <Button onClick={() => onClose()} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleApply} disabled={applying} size="small">
          {applying ? "Aplicando..." : `Aplicar em ${selected.length} gatilho(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Triggers = () => {
  const [triggers, setTriggers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [deletingTrigger, setDeletingTrigger] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: trgData }, { data: scrData }] = await Promise.all([
        api.get("/scriptTriggers"),
        api.get("/scripts"),
      ]);
      setTriggers(trgData || []);
      setScripts(scrData.scripts || scrData || []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleModalClose = (reload) => {
    setModalOpen(false);
    setSelectedTrigger(null);
    if (reload) loadData();
  };

  const handleToggle = async (trigger) => {
    try {
      const { data } = await api.put(`/scriptTriggers/${trigger.id}`, { active: !trigger.active });
      setTriggers(prev => prev.map(t => t.id === trigger.id ? { ...t, active: data.active } : t));
    } catch (err) { toastError(err); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/scriptTriggers/${deletingTrigger.id}`);
      toast.success("Gatilho excluído");
      setTriggers(prev => prev.filter(t => t.id !== deletingTrigger.id));
    } catch (err) { toastError(err); }
    setDeletingTrigger(null);
    setConfirmOpen(false);
  };

  const activeCount = triggers.filter(t => t.active).length;
  const filtered = triggers.filter(t =>
    !search ||
    (t.name || t.keyword || "").toLowerCase().includes(search.toLowerCase()) ||
    (t.script ? t.script.name : "").toLowerCase().includes(search.toLowerCase()) ||
    (t.keyword || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title="Excluir gatilho"
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Tem certeza? Este gatilho deixará de disparar automaticamente.
      </ConfirmationModal>

      <TriggerModal
        open={modalOpen}
        onClose={handleModalClose}
        trigger={selectedTrigger}
        scripts={scripts}
      />

      <BulkConfigModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        triggers={triggers}
        onApply={() => { setBulkOpen(false); loadData(); }}
      />

      <MainHeader>
        <Title>
          Gatilhos {triggers.length > 0 ? "(" + triggers.length + ")" : ""}
          {activeCount > 0 && (
            <Chip label={activeCount + " ativos"} size="small" color="success" sx={{ ml: 1, fontSize: "0.7rem" }} />
          )}
        </Title>
        <MainHeaderButtonsWrapper>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <TextField
              placeholder="Buscar gatilhos..."
              type="search" size="small" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search color="secondary" /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { height: 40 } }}
            />
            <Tooltip title="Configurar em lote">
              <Button variant="outlined" color="primary"
                onClick={() => setBulkOpen(true)}
                disabled={triggers.length === 0}
                sx={{ height: 40 }}>
                <SettingsIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Novo gatilho">
              <Button variant="contained" color="primary"
                onClick={() => { setSelectedTrigger(null); setModalOpen(true); }}
                sx={{ height: 40 }}>
                <AddCircleOutline />
              </Button>
            </Tooltip>
          </Box>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="center">Speed Mode</TableCell>
              <TableCell align="center">Condição</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    <BoltIcon sx={{ fontSize: "2.5rem", color: "text.disabled" }} />
                    <Typography variant="body2" color="textSecondary">
                      Nenhum gatilho cadastrado
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Crie gatilhos para disparar scripts automaticamente por palavra-chave
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(t => (
                <TriggerRow
                  key={t.id}
                  trigger={t}
                  onEdit={trig => { setSelectedTrigger(trig); setModalOpen(true); }}
                  onDelete={trig => { setDeletingTrigger(trig); setConfirmOpen(true); }}
                  onToggle={handleToggle}
                />
              ))
            )}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>
      </MainPaper>
    </MainContainer>
  );
};

export default Triggers;
